/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { InlineNotification, Link, Modal } from 'carbon-components-react';
import React from 'react';

import { PLAN_NAMES } from '@ibm-watson/shared/modules/ia/store/plan/constants';
import { isCrn } from '@ibm-watson/shared/modules/ia/util/crn';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import type { Noop } from '../../../../ts';
import { generateDocsUrl } from '../../util/docs';
import UpgradeInstanceModalPlan from './UpgradeInstanceModalPlan';

import './UpgradeInstanceModal.scss';

const links = {
  upgrade: generateDocsUrl('/?topic=watson-migrate')
};

const strings = {
  UPGRADE_MODAL_TITLE: 'Upgrade to get the features you need',
  UPGRADE_MODAL_TITLE1: 'Upgrade to unlock faster time to value',
  UPGRADE_MODAL_TITLE2:
    'Upgrade to unlock an end to end customer communication solution',
  UPGRADE_MODAL_TITLE3: 'Upgrade now to save your work',

  UPGRADE_MODAL_LITE_PRICE: 'Free',
  UPGRADE_MODAL_TRIAL_PRICE: '30 day free trial',
  UPGRADE_MODAL_PLUS_PRICE: 'Starts at $140',
  UPGRADE_MODAL_STANDARD_PRICE: '$0.0025 USD/message',
  UPGRADE_MODAL_ENTERPRISE_PRICE: 'Cost effective at scale',

  UPGRADE_MODAL_LITE_LIST_1:
    'Develop your lightweight assistant for free for an unlimited period of time',
  UPGRADE_MODAL_LITE_LIST_2:
    'Seamlessly digress to other topics during a conversational flow',
  UPGRADE_MODAL_LITE_LIST_3:
    'Save hours of development time using our web-chat client and service desk integrations',
  UPGRADE_MODAL_STANDARD_LIST_1:
    'Get basic functionality with unlimited messages',
  UPGRADE_MODAL_STANDARD_LIST_2:
    'Manage deployments of your assistant with versions',
  UPGRADE_MODAL_TRIAL_LIST_1:
    'Develop a powerful assistant using our advanced Plus plan features',
  UPGRADE_MODAL_TRIAL_LIST_2: 'Chat with up to 5,000 users within 30 days',
  UPGRADE_MODAL_PLUS_LIST_1:
    "Answer your customer's questions in real-time via phone, SMS and web",
  UPGRADE_MODAL_PLUS_LIST_2:
    'Train faster with intent recommendations which proposes new intents and highlights conflicts in your data',
  UPGRADE_MODAL_PLUS_LIST_3:
    "Use Search Skill to provide answers to your users' questions without the need for you to create explicit dialog",
  UPGRADE_MODAL_ENTERPRISE_LIST_1:
    'Ensure additional reliability and control with dedicated containers for data storage',
  UPGRADE_MODAL_ENTERPRISE_LIST_2:
    'Export, analyze and share logs anywhere you want via connected webhooks or API endpoints',
  UPGRADE_MODAL_ENTERPRISE_LIST_3:
    'Gain access to 90-day analytics retention period',
  UPGRADE_MODAL_ENTERPRISE_LIST_4:
    'Conduct regression testing without increasing MAU usage',

  UPGRADE_MODAL_START_TRIAL_BUTTON: 'Start free trial',
  UPGRADE_MODAL_PLUS_BUTTON: 'Upgrade to Plus',
  UPGRADE_MODAL_PLUS_BUTTON2: 'Upgrade to save work',
  UPGRADE_MODAL_ENTERPRISE_BUTTON: 'Contact for Enterprise',
  UPGRADE_MODAL_CURRENT: 'Current plan',
  UPGRADE_MODAL_COMPARE_LINK: 'View full feature comparison',
  PLAN_LITE: 'Lite',
  PLAN_PLUS_TRIAL: 'Trial',
  PLAN_STANDARD: 'Standard',
  PLAN_PLUS: 'Plus',
  PLAN_ENTERPRISE: 'Enterprise',
  CF_UPGRADE_TOOLTIP:
    'You cannot upgrade to a Plus or Trial plan until after you migrate your service instance from Cloud Foundry.',
  AT_UPGRADE_TOOLTIP:
    "Sorry, we're having trouble getting your account type, please try again later.",
  RECOMMENDED: 'Recommended',
  LEARN_MORE: 'Learn more'
};

export interface UpgradeInstanceModalProps {
  accountType: string;
  handleCloseClick: Noop;
  handleGetPricingClick: (pricing: string) => void;
  handleUpgradeButtonClick: Noop;
  instance: string;
  noTrial: boolean;
  planName: string;
}

export class UpgradeInstanceModal extends React.Component<UpgradeInstanceModalProps> {
  render() {
    const { instance, planName } = this.props;

    let title = strings.UPGRADE_MODAL_TITLE;
    if (planName === PLAN_NAMES.LITE) {
      title = strings.UPGRADE_MODAL_TITLE1;
    } else if (planName === PLAN_NAMES.STANDARD) {
      title = strings.UPGRADE_MODAL_TITLE2;
    } else if (planName === PLAN_NAMES.PLUS_TRIAL) {
      title = strings.UPGRADE_MODAL_TITLE3;
    }

    return (
      <Modal
        open
        size="lg"
        className="UpgradeInstanceModal"
        onRequestClose={this.handleClose}
        passiveModal={true}
        modalHeading={title}
        selectorPrimaryFocus=".bx--btn--primary">
        <div className="UpgradeInstanceModal__body">
          {!isCrn(instance) && (
            <InlineNotification
              kind="warning"
              hideCloseButton={true}
              title={strings.CF_UPGRADE_TOOLTIP}
              subtitle={
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={links.upgrade}>
                  {strings.LEARN_MORE}
                </Link>
              }
              statusIconDescription={strings.CF_UPGRADE_TOOLTIP}
            />
          )}
          <div className="UpgradeInstanceModal__plans">
            {this.renderCurrentPlan()}
            {this.renderUpgradePlan()}
            {this.renderEnterprisePlan(planName)}
          </div>
        </div>
        <div className="UpgradeInstanceModal__footer">
          <Link
            className="UpgradeInstanceModal__footer-item"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.ibm.com/cloud/watson-assistant/pricing"
            onClick={this.handleComparisonLink}>
            {strings.UPGRADE_MODAL_COMPARE_LINK}
          </Link>
        </div>
      </Modal>
    );
  }

  handleClose = () => {
    this.props.handleCloseClick();
    ctaEvents.upgradeModal({
      category: 'close',
      successFlag: true
    });
  };

  handleComparisonLink = () =>
    ctaEvents.featureCompare({ category: 'Upgrade Modal' });

  scheduleClick = () =>
    ctaEvents.bookConsultation({ category: 'Upgrade Modal' });

  renderCurrentPlan = () => {
    const { planName } = this.props;

    let titleKey, descriptionKey, featureKeys;

    if (planName === PLAN_NAMES.LITE) {
      titleKey = 'PLAN_LITE';
      descriptionKey = 'UPGRADE_MODAL_LITE_PRICE';
      featureKeys = [
        'UPGRADE_MODAL_LITE_LIST_1',
        'UPGRADE_MODAL_LITE_LIST_2',
        'UPGRADE_MODAL_LITE_LIST_3'
      ];
    } else if (planName === PLAN_NAMES.STANDARD) {
      titleKey = 'PLAN_STANDARD';
      descriptionKey = 'UPGRADE_MODAL_STANDARD_PRICE';
      featureKeys = [
        'UPGRADE_MODAL_STANDARD_LIST_1',
        'UPGRADE_MODAL_STANDARD_LIST_2'
      ];
    } else if (planName === PLAN_NAMES.PLUS_TRIAL) {
      titleKey = 'PLAN_PLUS_TRIAL';
      descriptionKey = 'UPGRADE_MODAL_TRIAL_PRICE';
      featureKeys = [
        'UPGRADE_MODAL_TRIAL_LIST_1',
        'UPGRADE_MODAL_TRIAL_LIST_2'
      ];
    }

    return (
      <UpgradeInstanceModalPlan
        strings={strings}
        type="current"
        title={titleKey}
        description={descriptionKey}
        features={featureKeys}
        buttonText="UPGRADE_MODAL_CURRENT"
      />
    );
  };

  renderUpgradePlan = () => {
    const {
      planName,
      instance,
      handleUpgradeButtonClick,
      accountType,
      noTrial
    } = this.props;

    let buttonMessage, toolTipMessage, buttonClick, buttonDisabled;

    const titleMessage = 'PLAN_PLUS';
    const descriptionMessage = 'UPGRADE_MODAL_PLUS_PRICE';
    const featuresMessages = [
      'UPGRADE_MODAL_PLUS_LIST_1',
      'UPGRADE_MODAL_PLUS_LIST_2',
      'UPGRADE_MODAL_PLUS_LIST_3'
    ];

    if (planName === PLAN_NAMES.LITE && !noTrial) {
      toolTipMessage = isCrn(instance) ? 'CF_UPGRADE_TOOLTIP' : '';
      buttonMessage = 'UPGRADE_MODAL_START_TRIAL_BUTTON';
      buttonClick = () => {
        handleUpgradeButtonClick();
        ctaEvents.upgradeModal({
          name: 'upgrade',
          category: 'plus trial',
          successFlag: true
        });
      };
      buttonDisabled = !isCrn(instance);
    } else {
      buttonMessage = noTrial
        ? 'UPGRADE_MODAL_PLUS_BUTTON'
        : 'UPGRADE_MODAL_PLUS_BUTTON2';
      buttonClick = () => {
        handleUpgradeButtonClick();
        ctaEvents.upgradeModal({
          name: 'upgrade',
          category: 'plus',
          successFlag: true
        });
      };
      buttonDisabled = !isCrn(instance) || !accountType;
      toolTipMessage = isCrn(instance)
        ? 'CF_UPGRADE_TOOLTIP'
        : !accountType
        ? 'AT_UPGRADE_TOOLTIP'
        : '';
    }

    return (
      <UpgradeInstanceModalPlan
        strings={strings}
        type="upgrade"
        tag={strings.RECOMMENDED}
        title={titleMessage}
        description={descriptionMessage}
        features={featuresMessages}
        buttonText={buttonMessage}
        toolTipText={toolTipMessage}
        onButtonClick={buttonClick}
        disabled={buttonDisabled}
      />
    );
  };

  renderEnterprisePlan = planName => {
    const { handleGetPricingClick } = this.props;

    const titleMessage = 'PLAN_ENTERPRISE';
    const featuresMessages = [
      'UPGRADE_MODAL_ENTERPRISE_LIST_1',
      'UPGRADE_MODAL_ENTERPRISE_LIST_2',
      'UPGRADE_MODAL_ENTERPRISE_LIST_3',
      'UPGRADE_MODAL_ENTERPRISE_LIST_4'
    ];

    const descriptionMessage =
      planName !== PLAN_NAMES.LITE && planName !== PLAN_NAMES.STANDARD
        ? 'UPGRADE_MODAL_ENTERPRISE_PRICE'
        : '';

    const buttonTextMessage = 'UPGRADE_MODAL_ENTERPRISE_BUTTON';
    const toolTipMessage = '';
    const buttonClick = () => {
      handleGetPricingClick('Enterprise column');
      ctaEvents.upgradeModal({
        name: 'upgrade',
        category: 'enterprise',
        successFlag: true
      });
    };

    return (
      <UpgradeInstanceModalPlan
        strings={strings}
        type="enterprise"
        title={titleMessage}
        description={descriptionMessage}
        features={featuresMessages}
        buttonText={buttonTextMessage}
        toolTipText={toolTipMessage}
        onButtonClick={buttonClick}
      />
    );
  };
}
